// Karte, IMG_4900 bis 4902 (Uebersicht) und IMG_4927 bis 4931 (mit
// Hervorhebung), 01.09.2026. Eigene Kreis-Polygone eingefaerbt nach
// Sammelstand, darunter eine selbst erzeugte Basiskarte.
//
// KEINE KARTENKACHELN: Der Projektplan schliesst einen Kachel-Anbieter in
// Paragraf 10.1 aus, und die Sicherheitskoepfe (deploy/nginx,
// "img-src 'self'") liessen ohnehin keine fremden Bilder durch - eine
// TileLayer-Zeile stand hier bis 11.09.2026 und hat live nie ein Bild
// geladen. An ihrer Stelle liegen jetzt Landflaechen, Grenzen, Seen und
// Namen aus /karte/basiskarte.json (data/build/basiskarte.mjs), damit die
// Karte aussieht wie die Vorlage: Meer blau, Nachbarlaender sichtbar,
// Laender- und Ortsnamen ueber den Kreisen.

import "leaflet/dist/leaflet.css";
import "../karte.css";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronLeft, Star, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GeoJSON, MapContainer, Pane, useMap, useMapEvents } from "react-leaflet";
import { canvas, DomEvent } from "leaflet";
import type { LatLngBoundsExpression, Layer, LeafletMouseEvent, StyleFunction } from "leaflet";
import type { Feature, FeatureCollection } from "geojson";
import { useBundle } from "../lib/bundle.js";
import { useEintragEntfernen, useEintragSetzenMitSpiegel, useEntries } from "../lib/entries.js";
import { aktiveSammlung } from "../lib/collections.js";
import { BASISKARTE_STIL, farbeFuer, KREIS_DECKKRAFT, regionFortschritt } from "../lib/karte.js";
import { useBasiskarte } from "../lib/basiskarte.js";
import { KartenBeschriftung } from "../bausteine/KartenBeschriftung.js";
import type { Nutzer, Sammlung } from "../lib/me.js";
import { useRegionsBundle, type Region } from "../lib/regions.js";

const DEUTSCHLAND_MITTE: [number, number] = [51.16, 10.45];

// Weiter hinaus als Zoom 3 zeigt die vereinfachte Geometrie nur noch
// Klumpen, naeher als 10 ihre Ecken. Die Schranke haelt die Karte
// ausserdem auf der Weltkugel: ohne sie laesst sich endlos nach Osten
// schieben, und dort ist nichts.
const ZOOM_MINDESTENS = 3;
const ZOOM_HOECHSTENS = 10;
const WELT: LatLngBoundsExpression = [
  [-85, -180],
  [85, 180],
];

// Stabile Referenz fuer den Fallback, solange das Bundle laedt, siehe
// derselbe Kommentar in Hauptscreen.tsx.
const LEERE_REGIONEN: Region[] = [];

/**
 * Klick auf eine leere Stelle der Karte (Meer, Ausland, Luecke zwischen
 * Kreisen) schliesst die Kennzeichen-Karte wieder. Ein Klick auf einen
 * Kreis selbst zaehlt nicht dazu: onEachFeature() unten stoppt dort die
 * Ausbreitung des Klicks, sonst wuerde jede Auswahl sich selbst sofort
 * wieder schliessen.
 */
function LeerflaecheSchliesst({ aktiv, onSchliessen }: { aktiv: boolean; onSchliessen: () => void }) {
  useMapEvents({
    click: () => {
      if (aktiv) onSchliessen();
    },
  });
  return null;
}

/**
 * Leaflet stellt seinem Nachweis von Haus aus "Leaflet" samt Flagge
 * voran. Der Nachweis selbst bleibt (Natural Earth), der Werbevorsatz
 * geht weg - er gehoert nicht in eine App, die keine fremden Kacheln
 * laedt.
 */
function OhneVorsatz() {
  const map = useMap();
  useEffect(() => {
    map.attributionControl.setPrefix(false);
  }, [map]);
  return null;
}

/**
 * Zeigt seinen Inhalt erst ab dieser Zoomstufe. Fluesse und Fernstrassen
 * sind bei Zoom 3 ueber ganz Europa ein Gestrichel, das nichts erklaert,
 * und jede Linie ist ein eigenes SVG-Element - 1962 davon kosten beim
 * Schieben spuerbar Zeit.
 */
function AbZoom({ ab, children }: { ab: number; children: ReactNode }) {
  const karte = useMap();
  const [zoom, setZoom] = useState(() => karte.getZoom());
  useMapEvents({ zoomend: () => setZoom(karte.getZoom()) });
  return zoom >= ab ? <>{children}</> : null;
}

/** Zentriert die Karte auf eine Region, sobald eine ausgewaehlt ist. */
function Zentrierung({ region }: { region: Region | undefined }) {
  const map = useMap();
  useEffect(() => {
    if (!region) return;
    const [minLng, minLat, maxLng, maxLat] = region.bbox;
    map.flyToBounds(
      [
        [minLat, minLng],
        [maxLat, maxLng],
      ],
      { duration: 0.5 },
    );
  }, [region, map]);
  return null;
}

// Heisst KartenSeite, nicht Karte: Der Name Karte ist schon die kleine
// Kartenflaeche aus bausteine/Bausteine.tsx.
export function KartenSeite({ nutzer, sammlungen }: { nutzer: Nutzer; sammlungen: Sammlung[] }) {
  const navigate = useNavigate();
  const [suchparameter] = useSearchParams();
  const vorausgewaehltePlateCodeId = useMemo(() => {
    const wert = suchparameter.get("plateCodeId");
    return wert ? Number(wert) : null;
  }, [suchparameter]);
  const [ausgewaehlt, setAusgewaehlt] = useState<string | null>(null);

  // Eine Leinwand fuer die ganze Basiskarte, einmal angelegt: ein neuer
  // Zeichner je Auffrischung haengte eine zweite Leinwand in die Ebene.
  // Er steht im Stil und nicht als eigene Eigenschaft, weil react-leaflet
  // alles, was kein `style` ist, an die GeoJSON-Optionen weiterreicht -
  // und dort kennt Leaflet keinen Zeichner.
  const basisZeichner = useMemo(() => canvas({ pane: "basis", padding: 0.3 }), []);

  const regionsBundle = useRegionsBundle();
  const basiskarte = useBasiskarte();
  const bundle = useBundle();
  const settings = (nutzer.settings ?? {}) as { activeCollectionId?: number; spiegleInEigene?: boolean };
  const sammlung = aktiveSammlung(sammlungen, settings.activeCollectionId);
  const eintraege = useEntries(sammlung?.id);
  // Der Stern in der Kreisliste ist derselbe wie auf dem Hauptscreen und
  // spiegelt deshalb genauso in die eigene Sammlung (lib/entries.ts).
  const { setzen: eintragSetzen } = useEintragSetzenMitSpiegel(
    sammlung,
    sammlungen,
    settings.spiegleInEigene,
  );
  const eintragEntfernen = useEintragEntfernen(sammlung?.id);

  const regionen = regionsBundle.data?.regionen ?? LEERE_REGIONEN;
  const karte = eintraege.data ?? new Map<number, number>();
  const kennzeichenById = useMemo(
    () => new Map((bundle.data?.kennzeichen ?? []).map((k) => [k.id, k])),
    [bundle.data],
  );

  // Sobald die Regionen da sind, die Region des mitgegebenen Treffers
  // einmalig vorauswaehlen (Karten-Link im Hauptscreen).
  useEffect(() => {
    if (vorausgewaehltePlateCodeId === null || ausgewaehlt !== null || regionen.length === 0) return;
    const treffer = regionen.find((r) => r.plateCodeIds.includes(vorausgewaehltePlateCodeId));
    if (treffer) setAusgewaehlt(treffer.ags);
  }, [regionen, vorausgewaehltePlateCodeId, ausgewaehlt]);

  const featureCollection = useMemo<FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: regionen.map((r) => ({
        type: "Feature",
        geometry: r.geomSimple,
        properties: { ags: r.ags },
      })),
    }),
    [regionen],
  );

  const regionByAgs = useMemo(() => new Map(regionen.map((r) => [r.ags, r])), [regionen]);

  function style(feature?: Feature): ReturnType<StyleFunction> {
    const ags = feature?.properties?.ags as string | undefined;
    const region = ags ? regionByAgs.get(ags) : undefined;
    const anteil = region && region.plateCodeIds.length > 0 ? regionFortschritt(region, karte).gesehen / region.plateCodeIds.length : 0;
    const istAusgewaehlt = ags === ausgewaehlt;
    return {
      fillColor: farbeFuer(anteil),
      // Nicht ganz deckend, damit Strassen, Fluesse und Seen der
      // Basiskarte durchscheinen wie in der Vorlage (lib/karte.ts).
      fillOpacity: KREIS_DECKKRAFT,
      // Schwarz, nicht blassgrau: in der Vorlage (und in den Bildern vom
      // 11.09.2026) ist reines Schwarz die haeufigste Farbe entlang der
      // Kreisgrenzen, in Deutschland wie in Polen und Tschechien. Solange
      // die Karte keinen Untergrund hatte, fiel das blasse Grau nicht auf.
      color: istAusgewaehlt ? "#FFFFFF" : "#000000",
      weight: istAusgewaehlt ? 3 : 1,
    };
  }

  function onEachFeature(feature: Feature, layer: Layer) {
    layer.on("click", (e: LeafletMouseEvent) => {
      // Sonst liefe der Klick zur Karte durch und LeerflaecheSchliesst
      // wuerde die gerade geoeffnete Auswahl im selben Moment wieder
      // schliessen.
      DomEvent.stopPropagation(e);
      setAusgewaehlt(feature.properties?.ags as string);
    });
  }

  const ausgewaehlteRegion: Region | undefined = ausgewaehlt ? regionByAgs.get(ausgewaehlt) : undefined;
  const fortschritt = ausgewaehlteRegion ? regionFortschritt(ausgewaehlteRegion, karte) : null;

  return (
    <div className="flex h-screen flex-col font-sans text-neutral-900">
      <header className="flex items-center gap-4 bg-akzent px-2 py-3 text-white shadow">
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Zurück"
          className="flex min-h-11 min-w-11 items-center justify-center"
        >
          <ChevronLeft size={26} />
        </button>
        <h1 className="text-lg font-medium">Gesehene Kennzeichen</h1>
      </header>

      <div className="relative flex-1">
        <MapContainer
          center={DEUTSCHLAND_MITTE}
          zoom={6}
          minZoom={ZOOM_MINDESTENS}
          maxZoom={ZOOM_HOECHSTENS}
          maxBounds={WELT}
          maxBoundsViscosity={1}
          className="karte-flaeche h-full w-full"
          scrollWheelZoom
        >
          {/* Basiskarte unter die Kreise. interactive={false}: ein Klick
              auf Frankreich soll die geoeffnete Kreisliste schliessen,
              nicht an der Landflaeche haengen bleiben.

              Gezeichnet wird sie auf eine Leinwand statt in SVG: Land,
              Grenzen, Seen, Fluesse und Fernstrassen sind zusammen rund
              8000 Linienzuege, und Leaflet haengt in SVG jede einzelne
              als eigenes Element in die Seite - auch die, die gerade
              ausserhalb des Bildes liegen. Die Kreise bleiben SVG: sie
              sind anklickbar und wechseln beim Auswaehlen ihren Rand. */}
          <Pane name="basis" className="karte-ebene-basis">
            {basiskarte.data && (
              <>
                <GeoJSON
                  data={basiskarte.data.land}
                  style={{ ...BASISKARTE_STIL.land, renderer: basisZeichner }}
                  interactive={false}
                  attribution="Kartengrundlage: Natural Earth"
                />
                <GeoJSON
                  data={basiskarte.data.binnengrenzen}
                  style={{ ...BASISKARTE_STIL.binnengrenze, renderer: basisZeichner }}
                  interactive={false}
                />
                {/* Fluesse vor die Seen: die Quelle fuehrt auch die Mittellinie
                    durch einen See, und die soll die Seeflaeche zudecken. */}
                <AbZoom ab={5}>
                  <GeoJSON
                    data={basiskarte.data.fluesse}
                    style={{ ...BASISKARTE_STIL.fluss, renderer: basisZeichner }}
                    interactive={false}
                  />
                </AbZoom>
                <GeoJSON
                  data={basiskarte.data.seen}
                  style={{ ...BASISKARTE_STIL.see, renderer: basisZeichner }}
                  interactive={false}
                />
                {/* Autobahnen ab Zoom 5, Fernstrassen erst ab 6 - dieselbe
                    Staffelung, die der Generator in abZoom schreibt. Zwei
                    Ebenen statt einer, weil Leaflet einen Filter nur beim
                    Anlegen auswertet. */}
                <AbZoom ab={5}>
                  <GeoJSON
                    data={basiskarte.data.strassen}
                    style={{ ...BASISKARTE_STIL.strasse, renderer: basisZeichner }}
                    filter={(feature) => feature.properties?.abZoom === 5}
                    interactive={false}
                  />
                </AbZoom>
                <AbZoom ab={6}>
                  <GeoJSON
                    data={basiskarte.data.strassen}
                    style={{ ...BASISKARTE_STIL.strasse, renderer: basisZeichner }}
                    filter={(feature) => feature.properties?.abZoom === 6}
                    interactive={false}
                  />
                </AbZoom>
              </>
            )}
          </Pane>
          {featureCollection.features.length > 0 && (
            <GeoJSON key={ausgewaehlt ?? "keine"} data={featureCollection} style={style} onEachFeature={onEachFeature} />
          )}
          <Pane name="beschriftung" className="karte-ebene-beschriftung">
            {basiskarte.data && <KartenBeschriftung basiskarte={basiskarte.data} />}
          </Pane>
          {vorausgewaehltePlateCodeId !== null && <Zentrierung region={ausgewaehlteRegion} />}
          <LeerflaecheSchliesst aktiv={ausgewaehlt !== null} onSchliessen={() => setAusgewaehlt(null)} />
          <OhneVorsatz />
        </MapContainer>

        {ausgewaehlteRegion && fortschritt && (
          <div
            // z-[1000]: Leaflets eigene Ebenen (Kacheln, Overlays,
            // Steuerung) liegen intern zwischen z-index 200 und 700. Ohne
            // eigenen, hoeheren Wert malt die Karte diese Karte zu, obwohl
            // sie im DOM danach kommt.
            className="absolute inset-x-0 bottom-0 z-[1000] max-h-[45%] overflow-y-auto rounded-t-lg bg-flaeche-inhalt shadow-lg"
          >
            <div className="flex items-start justify-between gap-2 border-b border-neutral-100 px-4 py-3">
              <div>
                <div className="text-lg font-semibold">{ausgewaehlteRegion.name}</div>
                <div className="text-sm text-neutral-500">
                  {fortschritt.gesehen} von {fortschritt.gesamt} gesehen
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAusgewaehlt(null)}
                aria-label="Schließen"
                className="flex min-h-11 min-w-11 shrink-0 items-center justify-center text-neutral-400"
              >
                <X size={22} />
              </button>
            </div>
            <ul>
              {ausgewaehlteRegion.plateCodeIds.map((id) => {
                const kennzeichen = kennzeichenById.get(id);
                const gesammelt = karte.has(id);
                if (!kennzeichen) return null;
                return (
                  <li key={id} className="flex items-center justify-between border-t border-neutral-100 px-4 py-2">
                    <span className="font-medium">{kennzeichen.code}</span>
                    <button
                      type="button"
                      onClick={() =>
                        gesammelt
                          ? eintragEntfernen.mutate(id)
                          : eintragSetzen({ plateCodeId: id, punkte: kennzeichen.points })
                      }
                      aria-label={gesammelt ? "Aus der Sammlung entfernen" : "Zur Sammlung hinzufügen"}
                      className="flex min-h-11 min-w-11 items-center justify-center text-black"
                    >
                      <Star size={22} fill={gesammelt ? "#FDD835" : "none"} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
